import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { CrashFile, CrashFileKind, World, WorldCrash } from '../../types';
import { Icon } from '../ui/Icon';
import { Empty, Loading, Note } from '../ui/primitives';
import { fmtBytes, logLevel } from '../../utils/format';
import { fmtLogTs, saveText, worldFilePrefix } from '../../utils/logs';

/** Lines drawn at once; a longer file shows its end and downloads in full. */
const MAX_LINES = 5000;

const KIND_LABEL: Record<CrashFileKind, string> = {
  LATEST_LOG: 'Latest log',
  CRASH_REPORT: 'Crash report',
  JVM_ERROR: 'JVM error log',
};

const crashKey = (c: WorldCrash) => `${c.serverId}/${c.timestamp}`;

const crashLabel = (c: WorldCrash) =>
  `${fmtLogTs(c.timestamp * 1000)} · ${c.exitCode != null ? `exit code ${c.exitCode}` : 'no exit code'} · server ${c.serverId}`;

/** Button labels for one crash's files; a second file of the same kind is numbered. */
const fileLabels = (files: CrashFile[]): Record<string, string> => {
  const labels: Record<string, string> = {};
  (Object.keys(KIND_LABEL) as CrashFileKind[]).forEach((kind) => {
    const ofKind = files.filter((f) => f.kind === kind);
    ofKind.forEach((f, i) => {
      labels[f.name] = ofKind.length > 1 ? `${KIND_LABEL[kind]} ${i + 1}` : KIND_LABEL[kind];
    });
  });
  return labels;
};

interface Props {
  world: World;
}

/**
 * What the world's crashed servers left behind, copied onto the control service before each server was deleted:
 * latest.log, Minecraft crash reports and JVM error logs. Pick a crash, then a file.
 */
const CrashLogs: React.FC<Props> = ({ world }) => {
  const [crashes, setCrashes] = useState<WorldCrash[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crashId, setCrashId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  // The last file fetched, keyed so a different pick never shows it while its own file loads.
  const [loaded, setLoaded] = useState<{ key: string; text?: string; error?: string } | null>(null);
  const termRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCrashes(await Worlds.crashes(world.id));
    } catch (e) {
      setError(errorMessage(e, 'Failed to load crashes.'));
    } finally {
      setLoading(false);
    }
  }, [world.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Newest crash and its first file (the latest log) unless the reader picked others.
  const crash = crashes.find((c) => crashKey(c) === crashId) || crashes[0];
  const file = crash?.files.find((f) => f.name === fileName) || crash?.files[0];
  const serverId = crash?.serverId;
  const timestamp = crash?.timestamp;
  const name = file?.name;
  const kind = file?.kind;
  const fileKey = serverId != null && timestamp != null && name != null ? `${serverId}/${timestamp}/${name}` : null;

  useEffect(() => {
    if (serverId == null || timestamp == null || name == null) return;
    const key = `${serverId}/${timestamp}/${name}`;
    let cancelled = false;
    Worlds.crashFile(world.id, serverId, timestamp, name)
      .then((t) => {
        if (!cancelled) setLoaded({ key, text: t });
      })
      .catch((e) => {
        if (!cancelled) setLoaded({ key, error: errorMessage(e, 'Failed to load the file.') });
      });
    return () => {
      cancelled = true;
    };
  }, [world.id, serverId, timestamp, name]);

  const current = loaded && loaded.key === fileKey ? loaded : null;
  const text = current?.text ?? null;
  const fileError = current?.error ?? null;
  const fileLoading = fileKey != null && current == null;

  const lines = useMemo(() => (text == null ? [] : text.replace(/\n$/, '').split('\n')), [text]);
  const shown = lines.length > MAX_LINES ? lines.slice(lines.length - MAX_LINES) : lines;

  // A log is read from where it stopped; a crash report or JVM error log from its summary at the top.
  useEffect(() => {
    const el = termRef.current;
    if (el) el.scrollTop = kind === 'LATEST_LOG' ? el.scrollHeight : 0;
  }, [text, kind]);

  if (loading && crashes.length === 0) return <Loading label="Loading crashes…" />;

  if (crashes.length === 0) {
    return (
      <>
        {error && <Note tone="danger">{error}</Note>}
        <Empty icon="skull" title="No crashes recorded">
          When a server of this world crashes, its latest log, crash reports and JVM error logs are copied here before the server is deleted.
        </Empty>
      </>
    );
  }

  const labels = fileLabels(crash.files);

  return (
    <>
      <div className="term-bar">
        <select
          className="input input--mono"
          aria-label="Crash"
          value={crashKey(crash)}
          onChange={(e) => {
            setCrashId(e.target.value);
            setFileName(null);
          }}
        >
          {crashes.map((c) => (
            <option key={crashKey(c)} value={crashKey(c)}>
              {crashLabel(c)}
            </option>
          ))}
        </select>
        {crash.files.length > 0 && (
          <div className="seg" role="group" aria-label="File">
            {crash.files.map((f) => (
              <button key={f.name} type="button" aria-pressed={f.name === name} onClick={() => setFileName(f.name)} title={f.name}>
                {labels[f.name]}
              </button>
            ))}
          </div>
        )}
        {file && (
          <span style={{ color: 'var(--ink3)', fontSize: 11.5 }}>
            {file.name} · {fmtBytes(file.size)}
            {lines.length > MAX_LINES && ` · last ${MAX_LINES.toLocaleString()} of ${lines.length.toLocaleString()} lines shown, Download has them all`}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn--sm" onClick={load} disabled={loading}>
          <Icon name="refresh" size={12} /> Refresh
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => file && text != null && saveText(`${worldFilePrefix(world)}-${crash.serverId}-${file.name}`, text)}
          disabled={text == null}
        >
          <Icon name="download" size={12} /> Download
        </button>
      </div>
      {error && <Note tone="danger">{error}</Note>}
      {fileError && <Note tone="danger">{fileError}</Note>}
      {crash.files.length === 0 ? (
        <Note tone="warn">Nothing could be copied off server {crash.serverId}: the box could not be reached, or it had no log files.</Note>
      ) : fileLoading ? (
        <Loading label="Loading file…" />
      ) : (
        text != null && (
          <div className="term" ref={termRef}>
            {shown.map((line, i) => {
              const lv = logLevel(line);
              return (
                <div key={i} className={lv === 'err' || lv === 'warn' ? `term-line lv-${lv}` : 'term-line'}>
                  {line || ' '}
                </div>
              );
            })}
          </div>
        )
      )}
    </>
  );
};

export default CrashLogs;
